import styled from 'styled-components';
import COLORS from '../../colors';
import { MONTH_PASCAL, TVNZ_LOGO_IMAGE_PATH } from '../../constants';
import { SummaryContainer } from '../containers';
import { useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/store';
import { useEffect, useState } from 'react';
import { getMonthsInRange } from '../../services/date';
import { mapPascalMonthToLowercaseMonth } from '../../util';

interface StyledItemProps {
  isOptimised?: boolean;
}

const StyledColumns = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  width: 100%;
  margin-bottom: 16px;
  margin-top: 16px;
`;

const Column = styled.div`
  flex: 1;
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
`;

const StyledTableHeading = styled.p`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 13.33px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  color: ${COLORS.NEUTRAL_500};
  padding: 0 12px;
`;

const StyledTableHeadingSmall = styled.p`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-size: 13.33px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  color: ${COLORS.NEUTRAL_500};
  padding: 0;
  max-width: 60px;
`;

const StyledLogoContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledLogoDescription = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 8px;
`;

const Logo = styled.img`
  width: 32px;
  height: 32px;
`;

const StyledLogoName = styled.h4`
  color: ${COLORS.NEUTRAL_100};
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 16px;
  margin: 0;
`;

const StyledLogoSubtext = styled.p`
  color: ${COLORS.NEUTRAL_500};
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
`;

const StyledItem = styled.div<StyledItemProps>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  color: ${COLORS.NEUTRAL_200};
  padding: 12px;
  background-color: ${(props) =>
    props.isOptimised ? 'transparent' : COLORS.NEUTRAL_925};
  border-radius: 8px;
`;

const StationSplit = () => {
  const monthData = useAppSelector((state: RootState) => state.calculated);

  const { startDate, endDate } = useAppSelector(
    (state: RootState) => state.detail.campaignLength
  );

  const [months, setMonths] = useState<string[]>([]);

  useEffect(() => {
    if (!startDate || !endDate) {
      return;
    }

    const monthsInRange = getMonthsInRange(
      new Date(startDate),
      new Date(endDate)
    );
    setMonths(monthsInRange);
  }, [startDate, endDate]);

  return (
    <SummaryContainer title="Station Split">
      <StyledColumns>
        <Column>
          <Row>
            <StyledTableHeadingSmall></StyledTableHeadingSmall>
            <StyledTableHeading>
              <StyledLogoContainer>
                <Logo src={TVNZ_LOGO_IMAGE_PATH.ONE} />
                <StyledLogoDescription>
                  <StyledLogoName>TVNZ 1</StyledLogoName>
                  <StyledLogoSubtext>Network</StyledLogoSubtext>
                </StyledLogoDescription>
              </StyledLogoContainer>
            </StyledTableHeading>
            <StyledTableHeading>
              <StyledLogoContainer>
                <Logo src={TVNZ_LOGO_IMAGE_PATH.TWO} />
                <StyledLogoDescription>
                  <StyledLogoName>TVNZ 2</StyledLogoName>
                  <StyledLogoSubtext>Network</StyledLogoSubtext>
                </StyledLogoDescription>
              </StyledLogoContainer>
            </StyledTableHeading>
            <StyledTableHeading>
              <StyledLogoContainer>
                <Logo src={TVNZ_LOGO_IMAGE_PATH.DUKE} />
                <StyledLogoDescription>
                  <StyledLogoName>TVNZ Duke</StyledLogoName>
                  <StyledLogoSubtext>Network</StyledLogoSubtext>
                </StyledLogoDescription>
              </StyledLogoContainer>
            </StyledTableHeading>
          </Row>
          {months.map((month) => {
            if (!month) {
              return;
            }

            const selectedMonth = mapPascalMonthToLowercaseMonth(
              month as MONTH_PASCAL
            );

            return (
              <Row>
                <StyledTableHeadingSmall>{month}</StyledTableHeadingSmall>
                {monthData[selectedMonth].stationLoading.one === 0 ? (
                  <StyledItem isOptimised>TVNZ Optimised</StyledItem>
                ) : (
                  <StyledItem>
                    {monthData[selectedMonth].stationSelectedValues.one}%
                  </StyledItem>
                )}
                {monthData[selectedMonth].stationLoading.two === 0 ? (
                  <StyledItem isOptimised>TVNZ Optimised</StyledItem>
                ) : (
                  <StyledItem>
                    {monthData[selectedMonth].stationSelectedValues.two}%
                  </StyledItem>
                )}
                {monthData[selectedMonth].stationLoading.duke === 0 ? (
                  <StyledItem isOptimised>TVNZ Optimised</StyledItem>
                ) : (
                  <StyledItem>
                    {monthData[selectedMonth].stationSelectedValues.duke}%
                  </StyledItem>
                )}
              </Row>
            );
          })}
        </Column>
      </StyledColumns>
    </SummaryContainer>
  );
};

export default StationSplit;
